import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import styles from './BranchDashboard.module.css';

const STATUS_BADGE = {
  'Pending':    styles.badgePending,
  'Confirmed':  styles.badgeConfirmed,
  'In Transit': styles.badgeOnWay,
  'On its way': styles.badgeOnWay,   // legacy
  'Delivered':  styles.badgeDelivered,
  'Cancelled':  styles.badgeCancelled,
};

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function BranchDashboard({ onNavigate }) {
  const { user }        = useAuth();
  const [orders, setOrders] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!user?.uid) return;

    const allQ = query(
      collection(db, 'orders'),
      where('branchId', '==', user.uid)
    );
    const unsubAll = onSnapshot(allQ, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const at = a.createdAt?.toMillis?.() ?? 0;
        const bt = b.createdAt?.toMillis?.() ?? 0;
        return bt - at;
      });
      setOrders(docs);
      setRecent(docs.slice(0, 5));
    });

    return unsubAll;
  }, [user?.uid]);

  const stats = [
    { label: 'Total Orders',  value: orders.length,                                              sub: 'All time'          },
    { label: 'Pending',       value: orders.filter(o => o.status === 'Pending').length,          sub: 'Awaiting dispatch' },
    { label: 'In Transit',    value: orders.filter(o => o.status === 'In Transit' || o.status === 'On its way').length, sub: 'In transit' },
    { label: 'Delivered',     value: orders.filter(o => o.status === 'Delivered').length,        sub: 'Completed'         },
  ];

  const totalSpent = orders
    .filter(o => o.status === 'Delivered')
    .reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={`Welcome back, ${user?.name}. Here's your branch overview.`}
      />

      <div className={styles.statsGrid}>
        {stats.map((s, i) => (
          <div key={i} className={styles.statCard}>
            <p className={styles.statLabel}>{s.label}</p>
            <p className={styles.statValue}>{s.value}</p>
            <p className={styles.statSub}>{s.sub}</p>
          </div>
        ))}
      </div>

      <div className={styles.grid}>
        {/* Recent Orders */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Recent Orders</h3>
            <span className={styles.cardMeta}>{orders.length > 0 ? `${orders.length} order(s) placed` : 'No orders yet'}</span>
          </div>
          {recent.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyText}>You haven't placed any orders yet.</p>
              <button className={styles.ctaBtn} onClick={() => onNavigate?.('orders')}>
                Place Your First Order
              </button>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(order => (
                  <tr key={order.id}>
                    <td className={styles.tdDetails}>{order.details}</td>
                    <td className={styles.tdTotal}>₱{order.total?.toLocaleString()}</td>
                    <td>
                      <span className={`${styles.badge} ${STATUS_BADGE[order.status] || ''}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className={styles.tdDate}>{formatDate(order.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Summary Info */}
        <div className={styles.cardCol}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Total Spent</h3>
            </div>
            <p className={styles.bigNumber}>₱{totalSpent.toLocaleString()}</p>
            <p className={styles.bigNumberSub}>Across {orders.filter(o => o.status === 'Delivered').length} delivered order(s)</p>
          </div>
        </div>
      </div>
    </>
  );
}
